// Chamadas do painel "Gestão Lisbonb" — instância própria de axios, deliberadamente à
// parte do `axios` global.
//
// O `AuthContext` guarda o JWT em `axios.defaults.headers.common`, que é um
// objecto GLOBAL partilhado por toda a SPA — incluindo a app Capacitor do
// ponto, que corre este mesmo bundle. Um interceptor de erro posto no axios
// global dispararia lá dentro também. É a mesma razão que levou o POS a ter a
// sua própria instância (`lib/pos.js`), e é a mesma solução.
//
// O token lê-se do `localStorage` A CADA PEDIDO, e não uma vez no arranque:
// quem entra com outra conta a meio da sessão do browser tem de ver o pedido
// seguinte a viajar com o token novo.
import axios from 'axios';
import { esperarBarreira, reentrar } from './sessao';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/bolso';

// **15 segundos, e não "para sempre".** Sem `timeout` o axios espera
// indefinidamente: no 4G de um café isso é um ecrã preso num spinner, sem uma
// palavra e sem saída. O painel é UM pedido só — se ele pendurar, não há mais
// nada no ecrã para olhar.
export const TIMEOUT_MS = 15000;

// **`no-cache` de propósito.** Isto é dinheiro, e o botão de recarregar tem
// de ir mesmo ao servidor: uma resposta servida do cache do browser mostra
// números velhos com um carimbo de agora, que é a pior combinação possível.
// Apanhado a medir — uma resposta chegou mais depressa do que o atraso que o
// servidor de ensaio tinha, e só o cache explica isso.
const api = axios.create({
  baseURL: API_URL,
  timeout: TIMEOUT_MS,
  headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
});

api.interceptors.request.use(async (config) => {
  // Com a barreira do Face ID fechada, nenhum pedido sai (ver lib/sessao.js).
  await esperarBarreira();
  let token = null;
  try { token = localStorage.getItem('token'); } catch (e) { /* modo privado */ }
  config.headers = config.headers || {};
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// **401 → uma re-entrada pelo Face ID e o MESMO pedido mais uma vez.** Aqui, e
// não em cada ecrã: todas as chamadas do bolso passam por esta instância.
// Sem Face ID (browser, build 2) a re-entrada diz logo «não» e o 401 segue
// para o ecrã como sempre seguiu.
api.interceptors.response.use(undefined, async (erro) => {
  const pedido = erro?.config;
  if (erro?.response?.status !== 401 || !pedido || pedido.repetido) throw erro;
  if (!(await reentrar())) throw erro;
  return api({ ...pedido, repetido: true });
});

// `empresa` é o id de uma empresa ou 'all' (o grupo); `unidade` é a loja, o
// terceiro nível, e só faz sentido DENTRO de uma empresa (o servidor recusa-a
// com 400 no grupo). Vai fora do objecto quando é nula para não mandar
// `unidade=null` na query string, que o FastAPI leria como a string "null".
//
// O servidor decide o que o âmbito quer dizer — 'all' soma só as empresas onde este utilizador é membro, e
// a resposta diz sempre QUAIS foram somadas.
export const getPainel = (empresa = 'all', unidade = null) =>
  api.get('/painel', { params: unidade ? { empresa, unidade } : { empresa } });

// --- Calendário das lojas (`/api/bolso/calendario`) --------------------------------------
// Caminhos por extenso, sempre: o `tests/fin/test_caminhos_do_bolso.py` lê-os daqui e
// confronta-os com as rotas do servidor (o POS já esteve morto em produção com a suite
// verde por um prefixo errado).
export const getCalendario = (ano, mes) => api.get('/calendario', { params: { ano, mes } });
export const getContagem = (ano) => api.get('/calendario/contagem', { params: { ano } });
// corpo: { datas: ['AAAA-MM-DD', …], location_id, user_ids: [uma ou duas], inicio: 'HH:MM', fim: 'HH:MM' }
export const criarIdas = (corpo) => api.post('/calendario/idas', corpo);
// corpo: { data, location_id, user_id, inicio, fim }
export const mudarIda = (id, corpo) => api.put(`/calendario/idas/${id}`, corpo);
export const apagarIda = (id) => api.delete(`/calendario/idas/${id}`);
// As trocas (só administradores). corpo: { data, sai, entra, inicio, fim } — `sai` e `entra` são user_id.
export const trocar = (corpo) => api.post('/calendario/trocas', corpo);
// corpo: { data, uma, outra } — duas pessoas da escala trocam de loja e de horas.
export const trocarEntreSi = (corpo) => api.post('/calendario/trocas/entre-si', corpo);
export const desfazerTroca = (trocaId) => api.delete(`/calendario/trocas/${trocaId}`);
export const getHistorico = (ano, mes) => api.get('/calendario/historico', { params: { ano, mes } });

// O dinheiro escreve-se sempre com duas casas e o símbolo à frente, como no
// resto do portal. **`null` não é zero**: um valor que o servidor não soube
// dizer sai "—" e nunca "0,00 €" — é a regra da casa, e é por isso que este
// formatador não copia o `eur()` do `lib/finance.js`, que faz `Number(n) || 0`
// e transformaria um "não sei" num zero com ar de facto.
export const euros = (valor) => {
  if (valor === null || valor === undefined || !Number.isFinite(Number(valor))) return '—';
  return `€ ${Number(valor).toLocaleString('pt-PT', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

// A percentagem, com o sinal à frente. `null` (não havia período anterior)
// devolve `null` — quem desenha decide o que mostrar, e nunca mostra "0%".
export const percentagem = (v) => {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return null;
  const n = Number(v);
  return `${n >= 0 ? '+' : '−'}${Math.abs(n).toLocaleString('pt-PT', {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  })}%`;
};

// "às 09:04" a partir do carimbo ISO que o servidor manda. Hoje mostra a hora;
// noutro dia mostra o dia e a hora, porque "às 09:04" sobre uma leitura de
// anteontem é uma meia-verdade que se lê como uma leitura fresca.
export const quandoFoi = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  const hora = d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  if (mesmoDia) return `às ${hora}`;
  return `${d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })} às ${hora}`;
};
